import React, { useMemo, useState, useCallback } from 'react';
import { Autocomplete, Button, iconButtonClasses, Link, Stack, TextField, Typography } from '@mui/material';
import { useCampaign, useNotifications } from 'hooks';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchInvoices, getInvoiceFileLink, markInvoiceAsPaid } from 'api/invoices';
import { InvoicePaymentStatus, Invoice } from 'types';
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowParams,
  GridToolbarContainer,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { Download, Upload, CheckCircle } from '@mui/icons-material';
import { getFormattedDateTime } from 'utils';
import { UploadInvoiceDialog } from './UploadInvoiceDialog';
import { Dialog } from 'components';

export const InvoiceList = () => {
  // pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const offset = paginationModel.page * paginationModel.pageSize;
  const limit = paginationModel.pageSize;
  // state-in-search
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCampaign = searchParams.get('campaignId');
  const setSelectedCampaign = (campaignId: string) => setSearchParams({ campaignId }, { replace: true });
  const clearSelectedCampaign = () => {
    searchParams.delete('campaignId');
    setSearchParams(searchParams, { replace: true });
  };
  // upload invoice dialog controls
  const [uploadInvoiceDialogOpen, setUploadInvoiceDialogOpen] = useState(
    searchParams.get('openUploadInvoice') === 'true'
  );
  // mark as paid confirmation dialog
  const [markAsPaidDialogOpen, setMarkAsPaidDialogOpen] = useState(false);
  const [invoiceToMarkAsPaid, setInvoiceToMarkAsPaid] = useState<Invoice | null>(null);

  // data fetching
  const { getAllCampaignsQuery } = useCampaign();
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();
  const getAllInvoicesQuery = useQuery({
    queryKey: ['allInvoices', offset, limit, selectedCampaign],
    queryFn: () =>
      fetchInvoices({
        all: true,
        offset,
        limit,
        campaignId: selectedCampaign ? Number(selectedCampaign) : null,
      }),
  });

  // Mark as paid mutation
  const markAsPaidMutation = useMutation({
    mutationFn: markInvoiceAsPaid,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['allInvoices'] });
      setNotification('Invoice marked as paid successfully', 'success');
      setMarkAsPaidDialogOpen(false);
      setInvoiceToMarkAsPaid(null);
    },
    onError: () => {
      setNotification('Failed to mark invoice as paid', 'error');
    },
  });

  const allCampaigns = getAllCampaignsQuery.data?.campaigns ?? [];
  const allInvoices = getAllInvoicesQuery.data?.invoices ?? [];
  const totalInvoices = getAllInvoicesQuery.data?.total;
  // table params
  const rowCountRef = React.useRef(totalInvoices || 0);
  const rowCount = React.useMemo(() => {
    if (totalInvoices !== undefined) {
      rowCountRef.current = totalInvoices;
    }
    return rowCountRef.current;
  }, [totalInvoices]);

  // Handle row click to open PDF in new tab
  const handleRowClick = (params: any) => {
    // Don't open PDF if user clicked on an action button
    if (params.field === 'actions') {
      return;
    }
    // Open PDF in new tab
    const pdfUrl = getInvoiceFileLink(params.row.id);
    window.open(pdfUrl, '_blank');
  };

  // Handle mark as paid
  const handleMarkAsPaidClick = useCallback((invoice: Invoice) => {
    setInvoiceToMarkAsPaid(invoice);
    setMarkAsPaidDialogOpen(true);
  }, []);

  const handleConfirmMarkAsPaid = () => {
    if (invoiceToMarkAsPaid) {
      markAsPaidMutation.mutate(invoiceToMarkAsPaid.id);
    }
  };

  const handleCloseMarkAsPaidDialog = () => {
    setMarkAsPaidDialogOpen(false);
    setInvoiceToMarkAsPaid(null);
  };

  const columns = useMemo(() => {
    return [
      { field: 'businessInvoiceId', headerName: 'Business invoice ID', flex: 0.15 },
      { field: 'campaignName', headerName: 'Campaign Name', flex: 0.2 },
      {
        field: 'invoiceDate',
        headerName: 'Created',
        flex: 0.15,
        valueFormatter: (value: string) => getFormattedDateTime(value),
      },
      { field: 'amount', headerName: 'Amount', flex: 0.15 },
      {
        field: 'paymentStatus',
        headerName: 'Paid',
        flex: 0.1,
        renderCell: (params: GridRenderCellParams<Invoice>) => (params.value === InvoicePaymentStatus.PAID ? '✓' : ''),
        align: 'center' as const,
      },
      {
        field: 'actions',
        // we need it because of funny DataGrid types
        type: 'actions' as const,
        getActions: ({ row }: GridRowParams) => {
          const actions = [
            <GridActionsCellItem
              icon={<Download />}
              component={Link}
              // @ts-ignore this think is purely typed, but component is still allowed and working
              href={getInvoiceFileLink(row.id)}
              showInMenu
              label="Download PDF"
            />,
          ];

          // Only show "Mark as Paid" for open invoices
          if (row.paymentStatus === InvoicePaymentStatus.OPEN) {
            actions.push(
              <GridActionsCellItem
                icon={<CheckCircle />}
                onClick={() => handleMarkAsPaidClick(row as Invoice)}
                showInMenu
                label="Mark as Paid"
              />
            );
          }

          return actions;
        },
      },
    ];
  }, [handleMarkAsPaidClick]);

  const Filter = () => (
    <Stack direction="row" gap={2} width="30%">
      <Autocomplete
        fullWidth
        value={allCampaigns.find((campaign) => campaign.id === Number(selectedCampaign)) ?? null}
        options={allCampaigns}
        isOptionEqualToValue={(option, value) => option === value}
        renderInput={(params) => <TextField placeholder="Select campaign" variant="outlined" {...params} />}
        getOptionLabel={(option) => `${option.name}`}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              <Stack spacing={1}>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="body3">ID: {option.id}</Typography>
              </Stack>
            </li>
          );
        }}
        onChange={(_, newValue, reason) => {
          if (reason === 'clear') {
            clearSelectedCampaign();
            return;
          }
          setSelectedCampaign(newValue?.id.toString() ?? '');
        }}
      />
    </Stack>
  );

  const UploadInvoiceButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        startIcon={<Upload />}
        onClick={() => setUploadInvoiceDialogOpen(true)}
      >
        Upload invoice
      </Button>
    );
  };

  return (
    <>
      <DataGridPro
        disableColumnFilter
        disableColumnSelector
        disableColumnSorting
        disableDensitySelector
        disableRowSelectionOnClick
        disableVirtualization
        columns={columns}
        rows={allInvoices}
        rowCount={rowCount}
        rowHeight={45}
        loading={getAllInvoicesQuery.isLoading}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        onCellClick={handleRowClick}
        sx={{
          [`& .${iconButtonClasses.root}`]: {
            padding: '5px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        }}
        slots={{
          toolbar: () => (
            <GridToolbarContainer sx={{ py: 4, px: 2 }}>
              <Stack direction="row" justifyContent="space-between" gap={2} width="100%">
                <Filter />
                <UploadInvoiceButton />
              </Stack>
            </GridToolbarContainer>
          ),
        }}
        slotProps={{
          basePopper: {
            sx: {
              '& .MuiPaper-root': {
                boxShadow:
                  '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12);',
              },
            },
          },
        }}
      />
      <UploadInvoiceDialog open={uploadInvoiceDialogOpen} onClose={() => setUploadInvoiceDialogOpen(false)} />
      <Dialog
        open={markAsPaidDialogOpen}
        title="Mark Invoice as Paid"
        content={
          invoiceToMarkAsPaid ? (
            <Typography>
              Are you sure you want to mark invoice "{invoiceToMarkAsPaid.businessInvoiceId}" as paid?
            </Typography>
          ) : null
        }
        primaryActionTitle="Mark as Paid"
        secondaryActionTitle="Cancel"
        onPrimaryActionClick={handleConfirmMarkAsPaid}
        onSecondaryActionClick={handleCloseMarkAsPaidDialog}
        isPrimaryActionLoading={markAsPaidMutation.isPending}
        isSecondaryActionDisabled={markAsPaidMutation.isPending}
      />
    </>
  );
};
